import React, { useRef, useState } from 'react';
import { createTemplateFromTargetGroup, errorLines } from '../../api';
import type { TargetGroup } from '../../types';
import { Button, InlineAlert, Modal, TextField, cx, useToast } from '../../ui';
import { useAuth } from '../auth/AuthProvider';

interface SaveAsTemplateDialogProps {
  open: boolean;
  targetGroup: TargetGroup | null;
  onClose: () => void;
}

/**
 * Save a target group's current settings (basics, regions, profiling, quotas) as a reusable template.
 * Scope: "My client" (the template belongs to the signed-in user's client) or company-wide (clientId null).
 */
export function SaveAsTemplateDialog({ open, targetGroup, onClose }: SaveAsTemplateDialogProps) {
  const { user } = useAuth();
  const { notify } = useToast();
  const clientId = user && typeof user.clientId === 'string' && user.clientId ? user.clientId : null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<'client' | 'company'>(clientId ? 'client' : 'company');
  const [nameError, setNameError] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  // Start fresh each time the dialog opens, named after the target group.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(targetGroup && targetGroup.name ? targetGroup.name : '');
      setDescription('');
      setScope(clientId ? 'client' : 'company');
      setNameError('');
      setProblems([]);
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current || !targetGroup) return;
    if (!name.trim()) { setNameError('Enter a name for the template.'); return; }
    setNameError('');
    savingRef.current = true;
    setSaving(true);
    setProblems([]);
    try {
      await createTemplateFromTargetGroup(targetGroup.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        clientId: scope === 'client' ? clientId : null,
      });
      notify('Template saved', { tone: 'ok' });
      onClose();
    } catch (err) {
      setProblems(errorLines(err));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const option = (value: 'client' | 'company', title: string, hint: string, disabled = false) => (
    <label className={cx('flex items-start gap-3 rounded-lg border px-3.5 py-3 text-sm', disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer', scope === value ? 'border-brand-300 bg-brand-50/50' : 'border-line')}>
      <input type="radio" name="template-scope" className="mt-0.5 h-4 w-4 accent-brand-600" checked={scope === value} disabled={disabled} onChange={() => setScope(value)} />
      <span><span className="block font-semibold text-ink">{title}</span><span className="block text-xs text-muted">{hint}</span></span>
    </label>
  );

  return (
    <Modal
      open={open}
      size="md"
      title="Save as template"
      subtitle={targetGroup ? targetGroup.name || 'Target group' : undefined}
      onClose={() => { if (!savingRef.current) onClose(); }}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form="save-template-form" loading={saving}>Save template</Button>
        </>
      )}
    >
      <form id="save-template-form" onSubmit={submit} noValidate className="space-y-5">
        <p className="text-sm text-muted">Saves this target group's basics, regions, profiling and quotas so you can start new target groups from it.</p>
        <TextField label="Template name" required autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} error={nameError} />
        <TextField label="Description" value={description} maxLength={240} onChange={(e) => setDescription(e.target.value)} hint="Optional. Shown in the list." />
        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-ink">Who can use it</legend>
          <div className="grid gap-2.5">
            {option('client', 'My client', clientId ? 'Only people in your client can start from it' : 'Not available: your login has no client', !clientId)}
            {option('company', 'Company-wide', 'Everyone can start from it')}
          </div>
        </fieldset>
        {problems.length > 0 && (
          <InlineAlert>{problems.length === 1 ? problems[0] : <><b>{problems[0]}</b><ul className="ml-4 mt-1 list-disc">{problems.slice(1).map((m) => <li key={m}>{m}</li>)}</ul></>}</InlineAlert>
        )}
      </form>
    </Modal>
  );
}
